import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import feedItemsApi from 'common/src/modules/feedItems/feedItems.api';

const setSpecificItemsToolSchema = z.object({
  itemIds: z.array(z.string()).describe('Array of specific product item IDs to use'),
});

export default function setSpecificItemsTool() {
  async function execute({
    input: {itemIds},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setSpecificItemsToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const feedId = validatedScratchpad.card.feedId;

    if (!feedId) {
      return toolError(`Feed is not set for this card`);
    }

    const strategy = validatedScratchpad.card.productsSelectionStrategy;

    if (strategy !== ProductSelectionStrategyType.SpecificProducts) {
      return toolError(`Strategy is not set to specific products for this card`);
    }

    const products = await feedItemsApi.getProductsByIds(feedId, itemIds);

    const existingProducts = products.filter((product) => product !== null);
    const existingProductIds = existingProducts.map((product) => product.id);
    const nonExistingIds = itemIds.filter((id) => !existingProductIds.includes(id));

    if (existingProducts.length === 0) {
      return toolError(`None of the provided item IDs exist in the feed`);
    }

    validatedScratchpad.card.productIds = existingProductIds;

    if (nonExistingIds.length > 0) {
      return toolResult(
        `Specific items set: ${existingProductIds.join(', ')}. The following IDs were not found and were not set: ${nonExistingIds.join(', ')}`,
      );
    }

    return toolResult(`Specific items set: ${existingProductIds.join(', ')}`);
  }

  return tool({
    name: V2CardAgentToolName.SetSpecificItems,
    displayName: 'Set specific items',
    description: 'Set specific product items to use',
    execute,
    inputSchema: setSpecificItemsToolSchema,
  });
}
